import { useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Rocket, Building2, Infinity as InfinityIcon } from "lucide-react";
import { fmtINR } from "../lib/api";
import { Input } from "./ui/input";

export const DEFAULT_PRICING = { starter_flat: 3000, starter_max: 30, growth_rate: 70, growth_max: 300, unlimited_rate: 50 };

/* Single source of truth for the 3-tier partner pricing model. */
export function computePlan(cfg = DEFAULT_PRICING, n = 0) {
  const c = { ...DEFAULT_PRICING, ...(cfg || {}) };
  const count = Math.max(0, Number(n) || 0);
  if (count === 0) return { key: "none", name: "No active websites", price: 0, perSite: 0 };
  if (count <= c.starter_max)
    return { key: "starter", name: "Starter", price: c.starter_flat, perSite: null, range: `1–${c.starter_max} websites`, note: "Flat monthly price — same for 1 or 30 websites" };
  if (count <= c.growth_max)
    return { key: "growth", name: "Growth", price: count * c.growth_rate, perSite: c.growth_rate, range: `${c.starter_max + 1}–${c.growth_max} websites`, note: `${fmtINR(c.growth_rate)} × total active websites` };
  return { key: "unlimited", name: "Unlimited", price: count * c.unlimited_rate, perSite: c.unlimited_rate, range: `${c.growth_max + 1}+ websites`, note: `${fmtINR(c.unlimited_rate)} × total active websites` };
}

export function planCards(cfg = DEFAULT_PRICING) {
  const c = { ...DEFAULT_PRICING, ...(cfg || {}) };
  return [
    { key: "starter", tag: "Starter", icon: Rocket, range: `1 – ${c.starter_max} Websites`, price: `₹${c.starter_flat.toLocaleString("en-IN")}`, unit: "/ month, flat",
      blurb: "One flat price whether you manage 1 or 30 websites.", popular: false,
      features: ["Fixed monthly bill — predictable", "All premium themes included", "Full store admin per client"] },
    { key: "growth", tag: "Growth", icon: Building2, range: `${c.starter_max + 1} – ${c.growth_max} Websites`, price: `₹${c.growth_rate}`, unit: "/ website / month",
      blurb: "Simple per-website rate on your total active count.", popular: true,
      features: ["Billed on total active websites", "Volume starts paying off", "Priority support"] },
    { key: "unlimited", tag: "Unlimited", icon: InfinityIcon, range: `${c.growth_max + 1}+ Websites`, price: `₹${c.unlimited_rate}`, unit: "/ website / month",
      blurb: "The lowest rate for large-scale agencies.", popular: false,
      features: ["Unlimited websites", "Best per-website rate", "Dedicated account manager"] },
  ];
}

export function PricingCards({ cfg, currentKey = null, onCta = null, compact = false }) {
  return (
    <div className={`grid sm:grid-cols-3 gap-6 ${compact ? "" : "mt-14"}`} data-testid="pricing-plans">
      {planCards(cfg).map((t) => {
        const active = currentKey === t.key;
        return (
          <div key={t.key} data-testid={`pricing-${t.key}`}
            className={`relative rounded-2xl p-7 h-full transition-all duration-300 hover:-translate-y-1.5 ${t.popular ? "g-border glow" : "glass-card hover:glow-sm"} ${active ? "ring-2 ring-emerald-500" : ""}`}>
            {t.popular && <span className="absolute -top-3 left-7 px-3 py-1 rounded-full gradient-bg text-white text-[10px] font-extrabold tracking-widest uppercase glow-sm">Most popular</span>}
            {active && <span className="absolute -top-3 right-7 px-3 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-extrabold tracking-widest uppercase" data-testid="pricing-current-badge">Your plan</span>}
            <div className="flex items-center gap-2.5">
              <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${t.popular ? "gradient-bg text-white" : "bg-muted"}`}><t.icon className="h-4 w-4" /></span>
              <p className="overline-label">{t.tag}</p>
            </div>
            <p className="font-display font-bold text-lg mt-3">{t.range}</p>
            <div className="flex items-baseline gap-1.5 mt-3">
              <span className="font-display font-extrabold text-4xl tracking-tighter text-gradient">{t.price}</span>
              <span className="text-sm text-muted-foreground">{t.unit}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">{t.blurb}</p>
            <div className="mt-5 space-y-2">
              {t.features.map((f) => (
                <p key={f} className="flex items-center gap-2 text-sm text-muted-foreground"><BadgeCheck className="h-4 w-4 text-accent shrink-0" />{f}</p>
              ))}
            </div>
            {onCta && (
              <button onClick={onCta} data-testid={`pricing-cta-${t.key}`}
                className={`w-full mt-7 h-11 rounded-full font-bold text-sm active:scale-95 transition-transform ${t.popular ? "gradient-bg text-white glow-sm" : "border border-border hover:bg-muted"}`}>
                Start Free Trial
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function PricingCalculator({ cfg, initial = 10, testidPrefix = "calc" }) {
  const [sites, setSites] = useState(initial);
  const plan = computePlan(cfg, sites);
  return (
    <div className="g-border rounded-2xl p-8 sm:p-10 glow" data-testid={`${testidPrefix}-calculator`}>
      <p className="overline-label">Interactive pricing calculator</p>
      <div className="flex flex-wrap items-center gap-8 mt-6">
        <div className="flex-1 min-w-64">
          <label className="text-sm font-bold">How many websites do you manage?</label>
          <div className="flex items-center gap-4 mt-3">
            <Input data-testid={`${testidPrefix}-input`} type="number" min="0" value={sites}
              onChange={(e) => setSites(Math.max(0, Number(e.target.value) || 0))}
              className="w-28 h-12 rounded-md text-center font-display font-extrabold text-lg" />
            <input data-testid={`${testidPrefix}-slider`} type="range" min="1" max="500" value={Math.min(500, Math.max(1, sites))}
              onChange={(e) => setSites(Number(e.target.value))}
              className="flex-1 h-2 rounded-full appearance-none cursor-pointer accent-[var(--p2)]" style={{ background: "linear-gradient(90deg, var(--p1), var(--p2))" }} />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-2"><span>1</span><span>{(cfg?.starter_max ?? 30)}</span><span>{(cfg?.growth_max ?? 300)}</span><span>500+</span></div>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Your package</p>
          <motion.p key={plan.key} initial={{ scale: 0.92, opacity: 0.4 }} animate={{ scale: 1, opacity: 1 }}
            data-testid={`${testidPrefix}-package`} className="font-display font-extrabold text-xl tracking-tight">{plan.name}</motion.p>
          <motion.p key={plan.price} initial={{ scale: 0.9, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }}
            data-testid={`${testidPrefix}-cost`} className="font-display font-extrabold text-5xl tracking-tighter text-gradient mt-1">{fmtINR(plan.price)}</motion.p>
          <p className="text-xs text-muted-foreground">/month{plan.perSite ? ` · ${sites} × ₹${plan.perSite}` : " · flat"}</p>
        </div>
      </div>
    </div>
  );
}
